import type { HelpTopic } from '../types';

export const progressTopics: HelpTopic[] = [
  {
    id: 'wms.progress.physical-milestones',
    title: 'Physical Milestones — Set Up & Update',
    icon: 'ri-hammer-line',
    summary: 'Define milestones for a work (one or many at a time) and capture on-site progress without an approval step.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p className="text-sm text-gray-700">
            Physical milestones break a work into weighted steps. Each milestone has a planned window, an achieved
            percentage, and supporting photos/remarks. Weighted completion feeds the work's overall progress
            (phscl_prgrs_nu) and unlocks linked financial milestones.
          </p>
        )
      },
      {
        id: 'setup-single',
        title: 'Set up milestones for a single work',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open the work's <span className="font-medium">Work Schedule</span> profile → Milestones tab.</li>
            <li>On first use you'll see three options: <span className="font-medium">Import from Masters</span>, <span className="font-medium">Import from Work</span>, or <span className="font-medium">+ Start from Scratch</span>.</li>
            <li>Masters picks a preconfigured template; Import from Work copies the milestone list from another work; Scratch lets you type them in.</li>
            <li>Edit the weightage % (should sum to 100), planned dates, and sequence. Save.</li>
          </ol>
        )
      },
      {
        id: 'setup-bulk',
        title: 'Set up for many works at once (Package Schedule)',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open a package from <span className="font-medium">Work Schedule → Package schedule</span>.</li>
            <li>Filter by UDA / Work Type / Scheme, or click the <span className="font-medium">Physical Not Set Up</span> card to see only works needing setup.</li>
            <li>Multi-select the rows — works that already have both schedules aren't selectable.</li>
            <li>Click <span className="font-medium">Set up Physical</span> in the purple action bar.</li>
            <li>One form opens with the same three options. Pick a template / source work / define milestones once — it's applied to every selected work in sequence with a per-work success/failure log.</li>
          </ol>
        )
      },
      {
        id: 'progress',
        title: 'Updating progress (no approval required)',
        body: (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              The separate approval step has been removed. Progress updates apply immediately regardless of
              schedule status, so PMU staff can record achieved % / photos / remarks whenever they come in.
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Progress Update tab → find the milestone.</li>
              <li>Enter achieved % (partial progress allowed if the milestone is configured for it), attach photos, remarks.</li>
              <li>Click <span className="font-medium">Submit Progress</span>. Overall work progress recalculates from weighted completion.</li>
            </ol>
          </div>
        )
      },
      {
        id: 'from-portal',
        title: 'From the vendor portal',
        body: (
          <p className="text-sm text-gray-700">
            Contractor users submit milestone updates from the vendor portal. Updates land directly on the work
            record (no approval gate on this side).
          </p>
        )
      }
    ],
    related: ['wms.progress.financial-milestones'],
    pageLinks: [
      { label: 'Open Work Schedule', to: '/app/ds-wms/work-schedule', icon: 'ri-calendar-check-line' }
    ]
  },
  {
    id: 'wms.progress.financial-milestones',
    title: 'Financial Schedule — Set Up & Billing',
    icon: 'ri-bill-line',
    summary: 'Plan payment targets (one or many at a time) and link running bills to physical progress.',
    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: (
          <p className="text-sm text-gray-700">
            The finance schedule lists the payment targets for a work — each with a target date, planned work-completed %,
            and planned payment amount (auto-computed from contract value). Actual payments/bills roll up into the work's
            financial progress (fncl_prgrs_nu).
          </p>
        )
      },
      {
        id: 'setup-single',
        title: 'Set up for a single work',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open the work's <span className="font-medium">Work Schedule → Finance Schedule</span> tab.</li>
            <li><span className="font-medium">Bulk Add Monthly</span> — generates N monthly entries (6 / 12 / 24 presets) with even % distribution across your chosen allocation. Each entry uses the last day of the month.</li>
            <li><span className="font-medium">Import from Work</span> — copies a source work's finance entries and recalculates ₹ amounts using this work's base value.</li>
            <li><span className="font-medium">+ Add Entry</span> — add rows manually (Specific Date / Month / Days-from-contract / Days-from-start modes).</li>
            <li>Total planned % should reach 100%. Save.</li>
          </ol>
        )
      },
      {
        id: 'setup-bulk',
        title: 'Set up for many works at once (Package Schedule)',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Open a package → filter / click <span className="font-medium">Financial Not Set Up</span> card to see eligible works.</li>
            <li>Multi-select rows (rows with both schedules already set are hidden from the checkbox — use row-click to edit them individually).</li>
            <li>Click <span className="font-medium">Set up Financial</span> in the action bar. The button is only enabled when selection contains at least one work still needing a finance schedule.</li>
            <li>One form opens with three options: <span className="font-medium">Bulk Monthly</span> (N months, evenly distributed), <span className="font-medium">Import from Work</span>, or <span className="font-medium">Start from Scratch</span>. Each work's ₹ amounts are calculated from its own contract value.</li>
          </ol>
        )
      },
      {
        id: 'billing-flow',
        title: 'Billing flow',
        body: (
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
            <li>Contractor raises a Running Bill against the next planned target.</li>
            <li>Check-measurement officer verifies; bill amount is fixed.</li>
            <li>Statutory + work-specific deductions apply automatically.</li>
            <li>Release updates <code>fncl_prgrs_nu</code>, Paid-to-Contractor, and Pending-to-be-Paid totals on the work.</li>
          </ol>
        )
      },
      {
        id: 'types',
        title: 'Payment types',
        body: (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
            <li>Mobilization advance (if applicable).</li>
            <li>Running bills (recurring).</li>
            <li>Final bill.</li>
            <li>Retention / security releases.</li>
          </ul>
        )
      }
    ],
    related: ['wms.progress.physical-milestones'],
    pageLinks: [
      { label: 'Open Payments', to: '/app/ds-wms/payments', icon: 'ri-bill-line' },
      { label: 'Open Work Schedule', to: '/app/ds-wms/work-schedule', icon: 'ri-calendar-check-line' }
    ]
  }
];
